import { useState } from 'react';
import {
  Users, CheckCircle, Clock, Search, X, UserPlus, RefreshCw, Ban, Building2
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  USER_STATUS_LABELS,
  type User,
  type UserStatus,
  type InviteUserRequest,
  type UpdateUserRequest,
} from '@/features/settings';
import { useSuppliers } from '@/features/suppliers';
import { useSites } from '@/features/settings';
import {
  UserRow, InviteUserModal, EditUserModal 
} from './components';

interface UsersViewProps {
  readonly users: User[];
  readonly isLoading: boolean;
  readonly userStats: {
    total: number;
    active: number;
    pending: number;
    disabled: number;
  };
  readonly userSearch: string;
  readonly setUserSearch: (search: string) => void;
  readonly userStatusFilter: UserStatus | undefined;
  readonly setUserStatusFilter: (status: UserStatus | undefined) => void;
  readonly onInviteUser: (data: InviteUserRequest) => Promise<void>;
  readonly onUpdateUser: (userId: string, data: UpdateUserRequest) => Promise<void>;
  readonly onUserAction: (userId: string, action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => Promise<void>;
  readonly onRefresh: () => void;
  readonly isFetching: boolean;
}

const DEFAULT_FORM_DATA: InviteUserRequest = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  jobTitle: '',
  department: '',
  role: 'venue-staff',
  siteAccess: [],
  venueAccess: [],
  supplierId: undefined,
  preferredLanguage: 'en',
};

export function UsersView({
  users,
  isLoading,
  userStats,
  userSearch,
  setUserSearch,
  userStatusFilter,
  setUserStatusFilter,
  onInviteUser,
  onUpdateUser,
  onUserAction,
  onRefresh,
  isFetching,
}: UsersViewProps) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteType, setInviteType] = useState<'internal' | 'supplier'>('internal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<InviteUserRequest>(DEFAULT_FORM_DATA);

  const { data: suppliersData } = useSuppliers();
  const { data: sitesData } = useSites({});
  
  const suppliers = suppliersData?.data ?? [];
  const sites = sitesData?.data ?? [];

  const supplierOptions = suppliers.map(s => ({
    value: s.supplierId,
    label: s.name 
  }));
  const siteOptions = sites.map(s => ({
    value: s.siteId,
    label: s.name 
  }));

  const handleOpenInvite = (type: 'internal' | 'supplier') => {
    setInviteType(type);
    const defaultRole = type === 'internal' ? 'venue-staff' : 'supplier-sales';
    setFormData({
      ...DEFAULT_FORM_DATA,
      role: defaultRole 
    });
    setShowInviteModal(true);
  };

  const handleInvite = async () => {
    setIsSubmitting(true);
    try {
      await onInviteUser(formData);
      setShowInviteModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const selectedSupplierName = selectedUser?.supplierId 
    ? suppliers.find(s => s.supplierId === selectedUser.supplierId)?.name 
    : undefined;

  return (
    <>
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        inviteType={inviteType}
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        supplierOptions={supplierOptions}
        siteOptions={siteOptions}
      />

      <EditUserModal
        user={selectedUser}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={onUpdateUser}
        onAction={onUserAction}
        supplierOptions={supplierOptions}
        siteOptions={siteOptions}
        supplierName={selectedSupplierName}
      />

      <UsersHeader
        onOpenInvite={handleOpenInvite}
        onRefresh={onRefresh}
        isFetching={isFetching}
      />

      <UsersStats stats={userStats} />

      <UsersFilters
        userSearch={userSearch}
        setUserSearch={setUserSearch}
        userStatusFilter={userStatusFilter}
        setUserStatusFilter={setUserStatusFilter}
      />

      <UsersList
        users={users}
        isLoading={isLoading}
        suppliers={suppliers}
        onEditUser={handleEditUser}
        onUserAction={onUserAction}
        onOpenInvite={handleOpenInvite}
      />
    </>
  );
}

interface UsersHeaderProps {
  readonly onOpenInvite: (type: 'internal' | 'supplier') => void;
  readonly onRefresh: () => void;
  readonly isFetching: boolean;
}

function UsersHeader({
  onOpenInvite, onRefresh, isFetching 
}: UsersHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => onOpenInvite('internal')}>
              <Users className="mr-2 h-4 w-4" />
              Invite Team Member
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOpenInvite('supplier')}>
              <Building2 className="mr-2 h-4 w-4" />
              Invite Supplier User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
        <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  );
}

interface UsersStatsProps {
  readonly stats: {
    total: number;
    active: number;
    pending: number;
    disabled: number;
  };
}

function UsersStats({ stats }: UsersStatsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
          <Users className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
          <CheckCircle className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          <Clock className="h-5 w-5 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Disabled</CardTitle>
          <Ban className="h-5 w-5 text-gray-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-600">{stats.disabled}</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface UsersFiltersProps {
  readonly userSearch: string;
  readonly setUserSearch: (search: string) => void;
  readonly userStatusFilter: UserStatus | undefined;
  readonly setUserStatusFilter: (status: UserStatus | undefined) => void;
}

function UsersFilters({
  userSearch,
  setUserSearch,
  userStatusFilter,
  setUserStatusFilter,
}: UsersFiltersProps) {
  const statuses: UserStatus[] = ['active', 'pending', 'disabled'];

  return (
    <div className="flex flex-wrap gap-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {userSearch && (
          <button
            type="button"
            onClick={() => setUserSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex gap-2">
        {statuses.map((status) => (
          <Button
            key={status}
            variant={userStatusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserStatusFilter(userStatusFilter === status ? undefined : status)}
          >
            {USER_STATUS_LABELS[status]}
          </Button>
        ))}
      </div>
    </div>
  );
}

interface UsersListProps {
  readonly users: User[];
  readonly isLoading: boolean;
  readonly suppliers: Array<{
    supplierId: string;
    name: string 
  }>;
  readonly onEditUser: (user: User) => void;
  readonly onUserAction: (userId: string, action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => Promise<void>;
  readonly onOpenInvite: (type: 'internal' | 'supplier') => void;
}

function UsersList({
  users,
  isLoading,
  suppliers,
  onEditUser,
  onUserAction,
  onOpenInvite,
}: UsersListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={`user-skeleton-${i.toString()}`} className="h-20 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">No users found</p>
          <Button variant="outline" className="mt-4" onClick={() => onOpenInvite('internal')}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite your first user
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {users.map((user) => (
            <UserRow
              key={user.userId}
              user={user}
              onEditUser={() => onEditUser(user)}
              onAction={onUserAction}
              supplierName={
                user.supplierId 
                  ? suppliers.find(s => s.supplierId === user.supplierId)?.name 
                  : undefined
              }
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
